<script setup lang="ts">
import { type PropType, computed, onUnmounted, watch, ref, onMounted } from 'vue'
import { CODE_OK } from '@/apis/code'
import { handleErr } from '@/composables/exception/handler'
import type { Ref } from 'vue'
import { ElMessage } from 'element-plus'
import { type AltarDTO } from '@/composables/life/life'
import { renderMessage } from '@/utils/util'
import { uploadFile } from '@/composables/media/media'
import { changeLifeAvatar } from '@/composables/life/life'
import { useRoute } from 'vue-router'
import type { FileDTO } from '@/composables/media/media'
import {
  THEME_LIGHT,
  globalShowMemorialPrivilegeDialog,
  globalAvatarUrl,
  globalName,
  globalInviteButtonRef,
  globalServiceId,
  globalYearPeriod,
  THEME_TYPE_PIC,
  THEME_TYPE_VIDEO
} from '@/composables/memorize/memorize'
import RToolTip from '@/components/base/RToolTip/RToolTip.vue'
import { titleBar } from '@/composables/menu/title'
import defaultAvatar from '@/assets/default_avatar.jpg'
import { rem2Px, roundToOneDecimal, minifyString } from '@/utils/util'
import MusicToggleButton from '@/components/base/theme/MusicToggleButton.vue'
import ThemeBackground from './ThemeBackground.vue'
import { previewTheme, themeServiceId, gThemeNow } from '@/composables/theme/theme'
import { useMusicSettings } from '@/composables/theme/theme'
import type MenuItem from '@/type/MenuItem'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import type { ComponentPublicInstance } from 'vue'
import ThemeButton from '@/components/base/theme/ThemeButton.vue'
import { isSetRealName, currentUserHandle, DEMO_ID } from '@/composables/user/users'
import { openLoading, closeLoading } from '@/components/business/loading/loading'
import { createAltar } from '@/composables/pixi/pixiGame'
import { useServicesStore } from '@/composables/memorize/store'
import { auditModeShow } from '@/utils/util'
import { useLocalStorage } from '@vueuse/core'
import ImageCropperPage from '@/pages/tools/ImageCropperPage.vue'
import PortraitSingleFaceFlow from '@/components/business/portrait/PortraitSingleFaceFlow.vue'
import { usePortraitStore } from '@/composables/portrait/store'
import xianglu from '@/assets/pixi/ui/xianglu.png'
import xiangluGray from '@/assets/pixi/ui/xianglu-gray.png'
import { isLogin } from '@/composables/user/users'
import SetRealNameDialog from '@/components/business/me/SetRealNameDialog.vue'
import PublicFigureIntroCard from '@/components/business/life/PublicFigureIntroCard.vue'
import { inMiniprograme } from '@/utils/util'
type PortraitSingleFaceFlowExpose = {
  selectNewImage: () => Promise<void>
  detectImageUrl: (url: string, successFile?: FileDTO) => Promise<void>
}
const realNameModalRef = ref<InstanceType<typeof SetRealNameDialog> | null>(null)
const MIDDLE_W = 350
const router = useRouter()
const { t } = useI18n()
const portraitStore = usePortraitStore()
const showRedBubble = useLocalStorage('showRedBubble', [])
const route = useRoute()
const props = defineProps({
  avatar: {
    type: Object as PropType<FileDTO | null>, // 允许 null
    default: null, // 默认值设为 null
    required: false // ✅ 不再强制要求
  },
  name: {
    type: String,
    default: ''
  },
  yearPeriod: {
    type: String,
    default: ''
  },
  isCreator: {
    type: Boolean,
    default: false
  },
  canManage: {
    type: Boolean,
    default: false
  },
  serviceId: {
    type: String,
    default: ''
  },
  altar: {
    type: Object as PropType<AltarDTO | null>,
    default: null
  },
  passAway: {
    type: Boolean,
    default: false
  }
})
const serviceDetailsStore = useServicesStore()
const menus = ref<MenuItem[]>([])
let cropVisible = ref(false)
let imagePath = ref('')
let canvasBlob: Ref<Blob | undefined> = ref()
const emit = defineEmits(['toLife', 'showTheme', 'showPrivilege', 'showVisits', 'updateAvatar'])
const portraitSingleFaceFlowRef = ref<PortraitSingleFaceFlowExpose | null>(null)

const isCreatorRole = computed(() => props.isCreator)
const canManage = computed(() => props.canManage)
const lifeData = computed(() => {
  return serviceDetailsStore.searchForItem(props.serviceId)?.life
})

const isPublicFigure = computed(() => {
  return Boolean(lifeData.value?.publicFigure)
})

const figureNameMap = computed<Record<string, string>>(() => ({
  politics_government: t('figure_category.politics_government'),
  military_defense: t('figure_category.military_defense'),
  culture_arts: t('figure_category.culture_arts'),
  sports: t('figure_category.sports'),
  academia_science_education: t('figure_category.academia_science_education'),
  business_finance: t('figure_category.business_finance'),
  entertainment_media_internet: t('figure_category.entertainment_media_internet'),
  law_medical_professionals: t('figure_category.law_medical_professionals'),
  social_charity: t('figure_category.social_charity'),
  religion_faith: t('figure_category.religion_faith'),
  adventure_extreme: t('figure_category.adventure_extreme'),
  historical: t('figure_category.historical'),
  famous: t('figure_category.famous')
}))

const figureCategoryName = computed(() => {
  const key = lifeData.value?.figureCategory
  if (!key) return ''
  return figureNameMap.value[key] || ''
})
const publicFigureIconRef = ref<HTMLElement | null>(null)
const publicFigureTipVisible = ref(false)
const publicFigureIntroDialogVisible = ref(false)

function showBindPhoneTips() {
  publicFigureTipVisible.value = false
  publicFigureIntroDialogVisible.value = true
}

function onTogglePublicFigureTip() {
  if (!isPublicFigure.value) return
  publicFigureTipVisible.value = !publicFigureTipVisible.value
}

const publicFigureInfo = computed(() => {
  if (!lifeData.value) return ''
  const date = new Date(lifeData.value.createdAt)
  const creatorName = lifeData.value.creator?.name.fullName || ''
  return t('m.public_figure_created_at', {
    name: creatorName,
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate()
  })
})

const inviteButtonRef = ref<ComponentPublicInstance | null>(null)
onMounted(() => {
  globalInviteButtonRef.value = inviteButtonRef as any
  if (route.query.invite) {
    toggleInviteDialog()
  }
})
const showMusicSetting = ref(false)
const musicSettings = useMusicSettings()

function onMusicSetting(settingServiceId?: string, play?: boolean) {
  let sId = settingServiceId || serviceId.value
  console.log('onMusicSetting:', settingServiceId, sId, play)
  if (sId) {
    if (play === true) {
      musicSettings.value[sId] = true
    } else {
      delete musicSettings.value[sId]
    }
  }
  showMusicSetting.value = false
}

function toggleInviteDialog() {
  globalShowMemorialPrivilegeDialog.value = !globalShowMemorialPrivilegeDialog.value
}

const toAltar = async () => {
  if (!isLogin.value) {
    showRedirectToLoginDialog.value = true
    return
  } else if (!isSetRealName.value) {
    const isSuccess = await (realNameModalRef.value as any)?.open()
    if (!isSuccess) {
      return
    }
  }
  if (serviceDetailsStore.searchForItem(serviceId.value)?.privilege?.permissions) {
    router.push('/altar/' + altar?.value?.id)
  } else {
    router.push('/altar/' + altar?.value?.id + '?static=true')
  }
}

//点击个人主页按钮后，气泡提示消失
const onClickLife = () => {
  if (showRedBubble.value.findIndex((item) => item === titleBar.value.life?.id) === -1) {
    showRedBubble.value.push(titleBar.value.life?.id)
  }
  emit('toLife')
}

/**
 * 判断是否需要显示气泡
 * 1. 已经去世
 * 2. 没点击过 （本地存储里没有记录）
 */
const showTip = computed(() => {
  return (
    props.passAway === true &&
    showRedBubble.value.findIndex((item) => item === titleBar.value.life?.id) === -1 &&
    lifeData.value.altar === null &&
    !auditModeShow.value
  )
})

const portraitUploadDialogVisible = ref(false)
// 处理裁剪后的图片

const showRedirectToLoginDialog = ref(false)

const serviceId = computed(() => {
  return props.serviceId
})
const nameRef = computed(() => {
  return props.name
})

const yearPeriodRef = computed(() => {
  console.log('!!!!!!!!!!yearPeriodRef:', props.yearPeriod)
  return props.yearPeriod
})

const avatarRef = computed(() => {
  return props.avatar
})

watch(
  () => props.avatar,
  (val) => {
    if (val) globalAvatarUrl.value = val?.highQuality || val?.middleQuality || val?.url || ''
  },
  { immediate: true }
)

watch(
  () => props.serviceId,
  (val) => {
    globalServiceId.value = val
  },
  { immediate: true }
)

watch(
  () => props.name,
  (val) => {
    globalName.value = val
  },
  { immediate: true }
)

watch(
  () => props.yearPeriod,
  (val) => {
    globalYearPeriod.value = val
  },
  { immediate: true }
)

watch(gThemeNow(), (newVal) => {
  titleBar.value.reverse = newVal?.mode !== THEME_LIGHT
})

const altar = computed(() => props.altar)

const hasPermission = computed(() => {
  const privilege = serviceDetailsStore.searchForItem(serviceId.value)?.privilege
  return privilege && privilege.permissions && privilege.permissions.length > 0
})

const windowWidth = ref(0)
const handleResize = () => {
  if (typeof window !== 'undefined') {
    windowWidth.value = window.innerWidth
  }
}
onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
})
function onPickImage(file: string) {
  imagePath.value = file
  cropVisible.value = true
}
function onCropResult(blob: Blob, width: number, height: number) {
  console.log('result - blob', blob, width, height)
  canvasBlob.value = blob
  handleCroppedImage(blob)
}
onMounted(() => {
  handleResize()
  console.log('MemorialPrivilegeDialogProps:', props.name)

  window.addEventListener('resize', handleResize)

  menus.value = [
    { name: t('action.edit_basics'), key: 'edit_avatar', icon: 'i-icon-park-outline:edit-name' } as MenuItem,
    { name: t('action.edit_birth_death'), key: 'edit_details', icon: 'i-bi:text-paragraph' } as MenuItem,
    { name: t('portrait.beautify_portrait'), key: 'edit_photo', icon: 'i-mdi:magic' } as MenuItem
  ]
  console.log('titleBar.value.life:', titleBar.value.life)
})

function onContextMenuClicked(item: MenuItem, index: number) {
  if (item.key === 'edit_avatar') {
    // 打开文件选择器
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = 'image/*'
    input.onchange = (event: any) => {
      const files = event.target.files
      if (files && files.length) {
        const file = files[0]
        if (/^image\/\w+/.test(file.type)) {
          imagePath.value = URL.createObjectURL(file)
          cropVisible.value = true
        } else {
          ElMessage.warning({
            message: renderMessage(t('exception.file.wrong_type'))
          })
        }
      }
    }
    input.click()
  } else if (item.key === 'edit_details') {
    // TODO修改
    router.push(
      '/guide/createLifeAndBaseInfo?edit=true&lifeId=' +
        lifeData.value?.id +
        '&realName=' +
        lifeData.value?.name.fullName
    )
  } else if (item.key === 'edit_photo') {
    portraitUploadDialogVisible.value = true
  }
}

const handleUseExistingPortraitAvatar = async () => {
  if (!avatarRef.value?.url) {
    await portraitSingleFaceFlowRef.value?.selectNewImage()
    return
  }
  await portraitSingleFaceFlowRef.value?.detectImageUrl(avatarRef.value.url, avatarRef.value)
}

const handleUploadNewPortraitImage = () => {
  void portraitSingleFaceFlowRef.value?.selectNewImage()
}

const handlePortraitSingleFaceSuccess = (file: FileDTO) => {
  portraitStore.setUploadFile(file)
  router.push({
    path: '/portrait/preview',
    query: {
      ...(lifeData.value?.id ? { lifeId: lifeData.value.id } : {}),
      ...(altar.value?.id ? { altarId: altar.value.id } : {})
    }
  })
}

onUnmounted(() => {
  closeLoading()
})

async function handleCroppedImage(imageBlob: Blob) {
  if (imageBlob) {
    const fileName: string = 'avatar.' + imageBlob?.type.split('/')[1]
    openLoading(t('portrait.waiting_for_avatar_setting'))
    const { data, error, statusCode } = await uploadFile(imageBlob, fileName)
    closeLoading()
    if (error.value) {
      handleErr(data, error, statusCode)
    } else {
      if (data.value?.code == CODE_OK) {
        console.log('data:', data.value)
        const fileId = data.value?.data.fileId as string
        emit('updateAvatar', data.value?.data.file)
        ElMessage({
          message: renderMessage(t('profile.change_avatar_success')),
          type: 'success'
        })
        changeAvatar(fileId)
      } else {
        ElMessage({
          message: renderMessage(data.value?.message || t('exception.server_error')),
          type: 'warning'
        })
      }
    }
  } else {
    ElMessage({
      message: renderMessage(t('create_life.no_avatar')),
      type: 'warning'
    })
  }
}

async function changeAvatar(fileId: string) {
  const { data, error, statusCode } = await changeLifeAvatar(titleBar.value.life?.id || '', fileId)
  if (error.value) {
    handleErr(data, error, statusCode)
  } else {
  }
}

onUnmounted(() => {
  titleBar.value.reverse = false
})

const themeHeight = computed(() => {
  const minHeight = rem2Px(78 / 4)
  const maxHeight = rem2Px(80 / 4)
  const mdHeight = rem2Px(70 / 4)
  const clampHeight = (height: number) => {
    const normalized = roundToOneDecimal(height)
    if (normalized < minHeight) return minHeight
    if (normalized > maxHeight) return maxHeight
    return normalized
  }
  if (windowWidth.value === 0) return 0
  if (windowWidth.value >= 768) return mdHeight
  const theme = gThemeNow().value
  if (theme && theme.mode == THEME_LIGHT) {
    if (theme.themeType == THEME_TYPE_PIC || theme.themeType == THEME_TYPE_VIDEO) {
      if (theme.file && theme.file.h > 0 && theme.file.w > 0) {
        const ratio = theme.file.w / theme.file.h
        if (ratio > 0) {
          let h = windowWidth.value / ratio
          return h
        }
      }
    } else {
      // gif
      if (theme.gifFile && theme.gifFile.h > 0 && theme.gifFile.w > 0) {
        const ratio = theme.gifFile.w / theme.gifFile.h
        if (ratio > 0) {
          let h = windowWidth.value / ratio
          return h
        }
      }
    }
  }
  return maxHeight
})
</script>
<template>
  <div flex="~ col" relative w-full items-center h-80 class="bg-theme-light" @click.stop.prevent="">
    <ClientOnly>
      <MusicToggleButton
        v-if="gThemeNow()?.value?.musicIds?.length > 0"
        z-10
        absolute
        top-4
        right-2
        :light="gThemeNow()?.value?.mode === THEME_LIGHT"
        :isPreview="previewTheme[themeServiceId] != null"
        :audioUrls="gThemeNow()?.value?.playList?.map((item) => item.file.url) || []"
        :serviceId="serviceId"
        :inSetting="showMusicSetting"
        @openSetting="showMusicSetting = true" />
      <MusicPlaySettingDialog v-model="showMusicSetting" :serviceId="serviceId" @finish="onMusicSetting" />
      <ThemeBackground v-if="gThemeNow().value" absolute bottom-0 left-0 right-0 top-0 :theme="gThemeNow().value" />
    </ClientOnly>
    <div
      flex="~ col"
      h-full
      w-full
      items-center
      pb-8
      select-none
      :class="isCreatorRole ? 'pt-10 md:pt-12' : 'pt-12'"
      @click.stop.prevent="">
      <div h-38 w-38 flex-r-center>
        <MemorizeAvatar
          v-if="!isCreatorRole"
          :src="avatarRef?.url || ''"
          :alt="name || defaultAvatar"
          :size="'xMedium'"
          :ratio="avatarRef && avatarRef.h > 0 ? avatarRef.w / avatarRef.h : 1">
        </MemorizeAvatar>
        <TitlebarMenuView :menus="menus" @onItemClicked="onContextMenuClicked" :width="200">
          <template #trigger>
            <MemorizeAvatar
              v-if="isCreatorRole"
              :src="avatarRef?.url || ''"
              :alt="name || defaultAvatar"
              :size="'xMedium'"
              :ratio="avatarRef && avatarRef.h > 0 ? avatarRef.w / avatarRef.h : 1">
            </MemorizeAvatar>
          </template>
        </TitlebarMenuView>
      </div>

      <div flex="~ col" w-full items-center class="theme-text-color select-text">
        <div relative mt-2 items-center justify-center>
          <div flex="~ row" items-center ref="publicFigureIconRef" @click.stop.prevent="onTogglePublicFigureTip">
            <h2 text-xl font-bold opacity-99 :class="isPublicFigure ? 'pl-2' : ''">
              {{ nameRef }}
            </h2>
            <span v-if="isPublicFigure" inline-block ml-0.5 mt-0.5 cursor-pointer i-ph:seal-check-bold text-base></span>
          </div>

          <RToolTip
            :wrapper-ref="publicFigureIconRef as any"
            placement="bottom"
            :arrow="true"
            v-model="publicFigureTipVisible"
            :style="'#000000'"
            :boxShadow="false">
            <div px-4 py-3 flex="~ col" w-max max-w-70 text-left text-white relative>
              <span
                class="h-4 w-4 p-1 text-third absolute top-5 right-5"
                i-ic:outline-info
                ml-4
                inline-block
                text-lg
                cursor-pointer
                @click.stop.prevent="showBindPhoneTips"></span>
              <div align-baseline>
                <span font-bold text-lg>{{ t('m.public_figure') }}</span>
                <span text-sm mt-1.5 text-third ml-2>{{ figureCategoryName }}</span>
              </div>
              <div font-normal mt-2 text-justify leading-6>{{ lifeData.figureSummary }}</div>
              <!-- <span text-sm mt-2.5 font-normal style="color: #d4b473">{{ publicFigureInfo }}</span> -->
            </div>
          </RToolTip>
        </div>
        <h3 text-4.2 opacity-90 mt-1.2>{{ yearPeriodRef }}</h3>
      </div>
    </div>
    <ClientOnly>
      <el-dialog
        v-model="publicFigureIntroDialogVisible"
        width="90%"
        :show-close="false"
        align-center
        class="custom-dialog max-w-120 bg-gradient-to-br from-#FFFBF1 to-white shadow-dark hover:shadow-dark duration-200">
        <template #header> </template>
        <div class="pt-2 px-2">
          <img
            src="https://lp-file.lifepage.cc/operation/20260511180409038_XwGl4tWJrk.png"
            class="absolute right-0 top-0 w-24 md:w-36 pointer-events-none select-none opacity-60" />
          <div class="relative z-10">
            <div class="text-xl font-bold text-main leading-6">
              {{ t('m.public_figure_intro_title') }}
            </div>
            <div class="mt-4 md:mt-6 text-sm text-second leading-6">
              <div>{{ t('m.public_figure_intro_p1') }}{{ t('m.public_figure_intro_p') }}</div>
            </div>
          </div>
        </div>
        <template #footer>
          <div class="w-full flex justify-center py-4">
            <button class="btn-outline px-4 py-2 rounded-lg min-w-36" @click="publicFigureIntroDialogVisible = false">
              {{ $t('action.close') }}
            </button>
          </div>
        </template>
      </el-dialog>
    </ClientOnly>
    <!-- :tipIcon="showTip && inMiniprograme ? true : false" -->
    <ClientOnly>
      <div absolute bottom-0 left-0 right-0 w-full py-2 flex="~ row" px-2 justify-between>
        <div flex="~ row" items-center>
          <ThemeButton
            v-show="isCreatorRole"
            :theme="gThemeNow()?.value?.mode == THEME_LIGHT"
            icon="i-ph:user-list"
            :text="$t('m.life_page')"
            @click.stop.prevent="onClickLife"
            mr-3
            :tip="!showTip ? '' : showTip && !inMiniprograme ? $t('altar.worship1') : $t('altar.worship3')" />
          <div v-if="windowWidth > 500" grow></div>
          <ThemeButton
            v-show="!auditModeShow && isCreatorRole"
            :theme="gThemeNow()?.value?.mode == THEME_LIGHT"
            :text="$t('theme.change_theme')"
            icon="i-carbon:color-palette"
            @click.stop.prevent="emit('showTheme')"
            mr-2 />
        </div>

        <ThemeButton
          ref="inviteButtonRef"
          v-show="canManage"
          :theme="gThemeNow()?.value?.mode == THEME_LIGHT"
          :text="$t('m.invite_friends')"
          icon="i-ix:add-user-filled"
          @click.stop.prevent="toggleInviteDialog" />
        <!-- <ThemeButton
            :theme="gThemeNow()?.value?.mode == THEME_LIGHT"
            text="2392"
            icon="i-mynaui:globe"
            :verticle="true"
            @click="emit('showVisits')" /> -->
      </div>
    </ClientOnly>
    <!-- </transition> -->
    <ClientOnly>
      <div w-auto absolute right-0 top-24>
        <ThemeButton
          v-show="altar && altar?.worshipInMemorial && !auditModeShow && (!lifeData?.openToPub || hasPermission)"
          :theme="gThemeNow()?.value?.mode == THEME_LIGHT"
          :text="!inMiniprograme ? $t('altar.altar') : ''"
          @click.stop.prevent="toAltar"
          verticle
          flatRight>
          <template #icon>
            <img
              class="w-8.5 h-8.5 mb-1"
              :class="{ 'mr-1.5': inMiniprograme }"
              :src="altar?.worshipStatus === 1 ? xianglu : xiangluGray" />
            <!-- <svg v-else xmlns="http://www.w3.org/2000/svg" width="2em" height="2em" viewBox="0 0 128 128">
              <path
                fill="#717f35"
                d="m51.9 100.22l-9.93-19.58s-1.68-11.01-.56-25.59c.98-12.72 3.45-21.67 10.06-29.64c5.13-6.18 13.29-12.45 21.4-15.39s8.95-3.08 8.95-2.1s-12.72 6.73-21.53 19.03S51.9 51 51.34 58.69s.56 41.53.56 41.53" />
              <path
                fill="#94a61d"
                d="M19.46 48.21c5.34 9.57 10.41 27.44 16.26 40.92c5.45 12.58 11.15 22.28 16.18 27.03s9.62 4.33 11.61 2.66c2.99-2.52 1.42-9.51 1.4-13.56c-.04-7.24 1.34-12.24 3.5-15.94c1.9-3.25 5.46-6.55 9.23-5.42c4.53 1.36 7.55 5.84 8.95 7.94s3.5 6.29 4.47 6.29s-.62-10.81-5.23-17.15c-4.56-6.27-11.11-7.5-16.99-4.94c-13.13 5.72-13.5 23.08-13.5 23.08s-8.32-7.01-14.05-25.32c-1.4-4.47-5.8-20.35-10.17-26.83C24.4 37 17.22 34.92 13.17 33.52s-8.95-2.8-9.09-1.96c-.14.85 9.37 5.88 15.38 16.65" />
              <path
                fill="#ffbd13"
                d="M59.44 105.59c-.4-2.78-1.66-10.05-1.16-19.18c.34-6.35 2.09-16.9 11.71-20.36c7.74-2.78 13.78-2.81 22.02 1.15c7.64 3.67 3.96-1.23.37-3.09c-2.49-1.29-13.1-6.65-23.61-3.08c-12.22 4.15-15.48 18.75-15.48 18.75s-.89-15.88 2.38-23.71c3.27-7.84 9.15-14.25 7.54-14.78c-.6-.2-8.02 2.83-12.2 12.4c-4.46 10.22-4.66 19.45-4.37 24.71s1.04 10.43 1.04 10.43s2.08 4.83 4.53 7.96c2.27 2.91 7.35 9.65 7.23 8.8" />
              <path
                fill="#ffbd13"
                d="M67.23 26.85c-.49-.1-5.69 1.9-8.31 8.18s-.39 11.46-.39 11.46s7-6.09 8.31-9.88c1.31-3.8 1.05-9.63.39-9.76" />
              <path
                fill="#ffbd13"
                d="M80.33 44.65c.07-.32-5.3-4.84-9.43-4.91c-4.12-.07-9.16 3.47-9.16 3.47s1.72 4.12 8.25 4.84c4.71.53 10.21-2.81 10.34-3.4M69.2 37.06c.46.39 5.76-.2 8.83-3.83c3.23-3.82 3.28-12.47 2.3-12.47c-1.17 0-1.96 1.24-3.99 2.49c-2.03 1.24-5.76 4.91-6.74 7.72c-.99 2.82-.86 5.7-.4 6.09m5.21.62c.11 1.16 4.21 4.99 8.76 5.19s8.57-1.52 8.57-2.2s-2.9-5.77-7.93-6.27c-5.44-.53-9.45 2.75-9.4 3.28m6.7-4.94c.54.93 6.8-.24 9.74-4.26c2.71-3.7 2.2-7.88 1.91-8.42s-4.65-.1-8.13 3.92c-2.88 3.33-4.01 7.9-3.52 8.76m6.51 0c-.03.88 2.59 4.46 5.58 5.83c4.5 2.06 10.23.83 10.48.05c.24-.78-2.45-6.06-6.07-7.64c-4.26-1.86-9.94.34-9.99 1.76m6.42-4.02c0 1.18 7.05.98 9.55-.73s5.09-6.32 5.68-6.95s.69-1.27-.59-1.66c-1.27-.39-7.54-.59-11.16 2.11s-3.48 6.99-3.48 7.23m7.93 1.57c-.65.37.34 1.62.83 2.64c.49 1.03 2.15 5.19 5.78 7c3.47 1.73 8.18 1.62 8.37 1.37c.2-.24-.82-6.06-4.36-8.71c-4.31-3.23-9.4-2.98-10.62-2.3m5.33-2.25c.12.57 4.9 3.08 8.96 3.43s8.08-3.72 8.13-4.16s-3.67-4.41-7.88-4.26c-5.53.19-9.35 4.3-9.21 4.99M89.39 49.68c-.08-.93-7.69-1.08-11.55 2.94c-3.87 4.01-3.97 7.83-3.97 7.83l2.79 1.81s5.34-2.35 8.22-5.14c3.36-3.25 4.6-6.32 4.51-7.44" />
              <path
                fill="#ffbd13"
                d="M82.46 75.52c1.23.05.98-5.14-.2-8.37c-1.25-3.43-4.55-5.87-4.55-5.87l-4.11.88s.09 4.07 2.07 7.6c2.63 4.66 5.66 5.71 6.79 5.76m1.15-12.53s-.41 4.19 1.52 8.08c2.01 4.06 6.41 6.41 7.39 6.02s.49-5.34-.15-7.69s-3.57-6.02-3.57-6.02zm-.54-2.05s3.13-5.09 6.95-6.66c5.45-2.24 12.39.54 12.43 1.57c.05 1.03-4.7 4.5-7.05 5.39c-2.35.88-7.15 1.71-7.15 1.71zm19.58.14c-4.16.18-8.27 2.55-8.37 3.87s3.57 4.26 8.71 4.26s8.32-3.43 8.32-3.92c.01-.49-2.93-4.45-8.66-4.21" />
              <path
                fill="#ffbd13"
                d="M95.51 66.78c-1.09-.2-2.25 5.34-.39 9.74c1.86 4.41 6.85 6.61 7.25 6.76c1.28.48.93-7.34-.59-10.53c-1.39-2.9-4.71-5.67-6.27-5.97m7.14 3.89c-.43.75.54 5.78 2.79 8.71c3.52 4.6 8.86 4.94 9.4 4.7s-.1-7.25-3.52-10.57c-3.29-3.19-8.28-3.53-8.67-2.84" />
            </svg> -->
          </template>
        </ThemeButton>
      </div>
    </ClientOnly>
    <PortraitUploadDialog
      v-model="portraitUploadDialogVisible"
      :avatar="avatarRef || undefined"
      @useExistingAvatar="handleUseExistingPortraitAvatar"
      @uploadNewImage="handleUploadNewPortraitImage"
      z-100 />

    <RediectToLoginDialog v-model="showRedirectToLoginDialog" />
    <SetRealNameDialog ref="realNameModalRef" />
    <PortraitSingleFaceFlow ref="portraitSingleFaceFlowRef" @success="handlePortraitSingleFaceSuccess" />
    <ClientOnly>
      <teleport to="body">
        <ImageCropperPage
          v-model="cropVisible"
          :path="imagePath"
          @onCropped="onCropResult"
          :threeToFour="true"
          class="fixed top-0 bottom-0 left-0 right-0 z-50 h-screen w-full" />
      </teleport>
    </ClientOnly>
  </div>
</template>
<style lang="scss" scoped>
.btn-small {
  @apply text-sm px-2 py-1.5 rounded;
}

.center-glow-light {
  text-shadow:
    0 0 15px #313131,
    0 0 110px #949494;
}

.center-glow-dark {
  text-shadow:
    0 0 12px #f3f3f3,
    0 0 110px #f6f6f6;
}

.theme-text-color {
  color: var(--theme-white);
  transition: color 0.3s ease;
}

:root[data-theme='light'] .theme-text-color {
  color: var(--theme-dark);
}

:root[data-theme='dark'] .theme-text-color {
  color: var(--theme-white);
}
</style>
